import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GRUPPEN_BESCHRIFTUNG, absoluterPfad, elternKnoten, passtZuSuche,
  type NavigationsEintrag,
} from '../navigation/struktur';
import './rahmen.css';

/**
 * Befehlspalette / globale Suche (Auftrag Abschnitt 13, Tastenkürzel nach
 * Abschnitt 29: Strg/Cmd+K, ESC schließt).
 *
 * ═══ WAS SIE FINDET - UND WARUM NICHT MEHR ══════════════════════════════
 *
 * Sie springt zu jedem Ziel der Navigationsbeschreibung
 * (`navigation/struktur.ts`), einschließlich der vom Server gelieferten
 * Fachbereiche des offenen Projekts. Das ist echt und sofort nützlich.
 *
 * Sie sucht NICHT in Räumen, Anlagen, Bauteilen oder Dokumenten. Der Grund
 * steht im Auftrag selbst: "Falls derzeit keine globale Backend-Suche
 * existiert: Frontend-Komponente / Architektur vorbereiten, aber KEINE
 * fingierten Ergebnisse erzeugen." Es gibt heute keinen übergreifenden
 * Suchendpunkt - jede Rubrik "Räume" wäre entweder leer oder erfunden.
 *
 * Vorbereitet ist sie dafür: `rubriken` nimmt weitere Trefferlisten
 * entgegen. Sobald ein Endpunkt existiert, wird er hier eingehängt, ohne
 * dass die Palette umgebaut werden muss. Fehlt eine Rubrik, entfällt sie
 * ganz - sie erscheint nicht als leere Überschrift.
 */
export interface PaletteTreffer {
  schluessel: string;
  beschriftung: string;
  /** Zeile darunter: Gruppe, Kennung, Modell - was den Treffer einordnet. */
  einordnung?: string;
  pfad: string;
}

export interface PaletteRubrik {
  titel: string;
  treffer: PaletteTreffer[];
}

export interface CommandPaletteProps {
  offen: boolean;
  onSchliessen: () => void;
  eintraege: NavigationsEintrag[];
  projektId?: string;
  /** Zusätzliche Rubriken aus echten Endpunkten. Heute leer, siehe oben. */
  rubriken?: PaletteRubrik[];
}

export function CommandPalette({
  offen, onSchliessen, eintraege, projektId, rubriken = [],
}: CommandPaletteProps) {
  const [suche, setSuche] = useState('');
  const [markiert, setMarkiert] = useState(0);
  const feld = useRef<HTMLInputElement>(null);
  const liste = useRef<HTMLDivElement>(null);
  const gehZu = useNavigate();

  /*
   * Die Einordnung nennt das GEWERK, nicht die Gruppe.
   *
   * "Schall — Berechnungen" sagte nicht, wessen Schall; "Schall — Lüftung"
   * sagt es. Nur wo ein Eintrag zu keinem Gewerk gehoert (Zustand,
   * Auftraege, Normwerte), bleibt die Gruppe stehen - sonst stuende dort
   * gar nichts.
   */
  const navigationsTreffer = useMemo<PaletteTreffer[]>(
    () => eintraege
      .filter((e) => passtZuSuche(e, suche))
      .map((e) => ({
        schluessel: e.schluessel,
        beschriftung: e.beschriftung,
        einordnung: elternKnoten(eintraege, e)?.beschriftung
          ?? GRUPPEN_BESCHRIFTUNG[e.gruppe],
        pfad: absoluterPfad(e, projektId),
      })),
    [eintraege, suche, projektId],
  );

  // Nur Rubriken mit Treffern - eine leere Überschrift "Räume (0)" wäre das
  // Versprechen einer Suche, die es nicht gibt.
  const alleRubriken = useMemo<PaletteRubrik[]>(() => ([
    { titel: 'Navigation', treffer: navigationsTreffer },
    ...rubriken,
  ].filter((r) => r.treffer.length > 0)), [navigationsTreffer, rubriken]);

  const flach = useMemo(
    () => alleRubriken.flatMap((r) => r.treffer),
    [alleRubriken],
  );

  // Beim Öffnen zurücksetzen und den Fokus ins Feld legen (Abschnitt 28:
  // "Dialog-Fokus").
  useEffect(() => {
    if (!offen) return;
    setSuche('');
    setMarkiert(0);
    // Ein Tick später: das Feld existiert erst nach dem Rendern.
    const id = window.setTimeout(() => feld.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [offen]);

  useEffect(() => { setMarkiert(0); }, [suche]);

  // Die Markierung im Blick halten, wenn man mit den Pfeiltasten wandert.
  useEffect(() => {
    liste.current
      ?.querySelector('[data-markiert="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [markiert]);

  if (!offen) return null;

  function springen(pfad: string) {
    onSchliessen();
    gehZu(pfad);
  }

  function beiTaste(ereignis: React.KeyboardEvent) {
    if (ereignis.key === 'Escape') {
      ereignis.preventDefault();
      onSchliessen();
      return;
    }
    if (ereignis.key === 'ArrowDown') {
      ereignis.preventDefault();
      setMarkiert((v) => (flach.length === 0 ? 0 : (v + 1) % flach.length));
      return;
    }
    if (ereignis.key === 'ArrowUp') {
      ereignis.preventDefault();
      setMarkiert((v) => (flach.length === 0 ? 0 : (v - 1 + flach.length) % flach.length));
      return;
    }
    if (ereignis.key === 'Enter' && flach[markiert]) {
      ereignis.preventDefault();
      springen(flach[markiert].pfad);
    }
  }

  let laufend = -1;

  return (
    // Klick auf den Hintergrund schließt - dasselbe Verhalten wie bei den
    // vorhandenen Dialogen in `styles.css`.
    <div
      className="palette-hintergrund"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onSchliessen(); }}
    >
      <div
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-label="Suchen und springen"
        onKeyDown={beiTaste}
      >
        <input
          ref={feld}
          type="text"
          className="palette-feld"
          placeholder="Seite, Gewerk oder Berechnung suchen …"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          aria-label="Suchbegriff"
          aria-controls="palette-treffer"
          autoComplete="off"
        />

        <div className="palette-treffer" id="palette-treffer" role="listbox" ref={liste}>
          {flach.length === 0 && (
            <div className="palette-leer">
              Kein Navigationsziel zu „{suche}“.
              <div className="palette-leer-hinweis">
                Die Suche umfasst Seiten und Gewerke. Eine Suche über Räume,
                Anlagen und Bauteile gibt es noch nicht.
              </div>
            </div>
          )}

          {alleRubriken.map((rubrik) => (
            <div className="palette-rubrik" key={rubrik.titel}>
              <div className="palette-rubrik-titel">
                {rubrik.titel} ({rubrik.treffer.length})
              </div>
              {rubrik.treffer.map((treffer) => {
                laufend += 1;
                const ist = laufend === markiert;
                const index = laufend;
                return (
                  <button
                    key={treffer.schluessel}
                    type="button"
                    role="option"
                    aria-selected={ist}
                    data-markiert={ist}
                    className={`palette-punkt${ist ? ' markiert' : ''}`}
                    onMouseEnter={() => setMarkiert(index)}
                    onClick={() => springen(treffer.pfad)}
                  >
                    <span className="palette-punkt-text">{treffer.beschriftung}</span>
                    {treffer.einordnung && (
                      <span className="palette-punkt-einordnung">{treffer.einordnung}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="palette-fuss">
          <span><kbd>↑</kbd><kbd>↓</kbd> wählen</span>
          <span><kbd>Enter</kbd> öffnen</span>
          <span><kbd>Esc</kbd> schließen</span>
        </div>
      </div>
    </div>
  );
}
